type FillEntry = {
  inFlight: Promise<void> | null;
  sent: number;
  wanted: number;
  applied: number;
  error: unknown;
};

type FillSchedulerOptions<T> = {
  send: (documentId: string, revision: number) => Promise<T>;
  onResult: (documentId: string, result: T) => void;
  onError: (documentId: string, error: unknown) => void;
};

export type FillScheduler = {
  request: (documentId: string, revision: number) => void;
  isIdle: () => boolean;
  dispose: () => void;
  finish: (documentIds: string[], revision: number) => Promise<void>;
};

export const createFillScheduler = <T>({
  send,
  onResult,
  onError,
}: FillSchedulerOptions<T>): FillScheduler => {
  const entries: Record<string, FillEntry> = {};
  const lifecycle = { disposed: false, finishing: false };
  const entryFor = (documentId: string) => {
    entries[documentId] ??= {
      inFlight: null,
      sent: 0,
      wanted: 0,
      applied: 0,
      error: null,
    };
    return entries[documentId];
  };

  const sendNow = (documentId: string, revision: number) => {
    const entry = entryFor(documentId);
    entry.sent = revision;
    const call = send(documentId, revision)
      .then((result) => {
        if (!lifecycle.disposed && entry.sent === revision) {
          onResult(documentId, result);
          entry.applied = revision;
          entry.error = null;
        }
      })
      .catch((error: unknown) => {
        if (!lifecycle.disposed && entry.sent === revision) {
          entry.error = error;
          onError(documentId, error);
        }
      })
      .finally(() => {
        if (entry.inFlight === call) {
          entry.inFlight = null;
        }
        if (!lifecycle.finishing) {
          run(documentId);
        }
      });
    entry.inFlight = call;
  };

  const run = (documentId: string) => {
    const entry = entryFor(documentId);
    if (lifecycle.disposed || entry.inFlight || entry.wanted <= entry.sent) {
      return;
    }
    sendNow(documentId, entry.wanted);
  };

  return {
    request: (documentId, revision) => {
      if (lifecycle.finishing) {
        return;
      }
      const entry = entryFor(documentId);
      entry.wanted = Math.max(entry.wanted, revision);
      run(documentId);
    },
    isIdle: () =>
      Object.values(entries).every(
        (entry) => !entry.inFlight && entry.wanted <= entry.sent,
      ),
    dispose: () => {
      lifecycle.disposed = true;
    },
    finish: async (documentIds, revision) => {
      lifecycle.finishing = true;
      try {
        await Promise.all(
          documentIds.map(async (documentId) => {
            const entry = entryFor(documentId);
            entry.wanted = Math.max(entry.wanted, revision);
            // The server accepts final text before waiting for the document lock.
            if (entry.sent < revision) {
              sendNow(documentId, revision);
            }
            await entry.inFlight;
            if (
              entry.applied < revision &&
              entry.error &&
              !lifecycle.disposed
            ) {
              sendNow(documentId, revision);
              await entry.inFlight;
            }
            if (lifecycle.disposed || entry.applied < revision || entry.error) {
              throw (
                entry.error ?? new Error('The final transcript was not applied')
              );
            }
          }),
        );
      } finally {
        lifecycle.disposed = true;
      }
    },
  };
};
